import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Typography from 'LuxKit/Typography/Typography'
import useLoyaltyConfig from 'loyalty/hooks/useLoyaltyConfig'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import { rem } from 'polished'
import clsx from 'clsx'
import React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import TierUpgradeCampaignTierCard from './TierUpgradeCampaignTierCard'

const Section = styled(Pane)`
  padding: ${rem(32)} 0;

  ${mediaQueryUp.tablet} {
    padding: ${rem(64)} 0;
  }
`

const CardsGrid = styled.div`
  display: grid;
  gap: ${rem(24)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(2, 1fr);
    gap: ${rem(34)};
  }

  ${mediaQueryUp.desktop} {
    &.columns-2 { grid-template-columns: repeat(2, 1fr); }
    &.columns-3 { grid-template-columns: repeat(3, 1fr); }
  }
`

interface Props {
  campaign: App.TierUpgradeCampaignPayload
  onCtaClick?: () => void
  ctaText?: string
}

function TierUpgradeCampaignTierCards(props: Props) {
  const intl = useIntl()
  const { campaign, onCtaClick, ctaText } = props
  const [config] = useLoyaltyConfig()

  const tierBenefitsMap = config?.regionTierBenefits

  const eligibilityLabel = intl.formatMessage(
    {
      id: '1jIgyAacBHnngd1BDLeu',
      defaultMessage: 'Eligible for {partnerName} {memberStatus}s',
      description: 'Eligibility label shown on each tier card on the tier upgrade campaign page',
    },
    { partnerName: campaign.partner.name, memberStatus: campaign.partner.memberStatus },
  )
  if (!tierBenefitsMap || !campaign.secondarySection) return null

  const { title: sectionTitle, subtitle: sectionSubtitle } = campaign.secondarySection

  return <Section>
    <LayoutContainer variant="medium">
      <VerticalSpacer gap={32}>
        <VerticalSpacer gap={12}>
          <Heading variant="heading2" align="center">{sectionTitle}</Heading>
          <BodyText variant="large" align="center">{sectionSubtitle}</BodyText>
        </VerticalSpacer>
        <CardsGrid className={clsx({ [`columns-${campaign.tiers.length}`]: campaign.tiers.length <= 3 })}>
          {campaign.tiers.map(tier => <TierUpgradeCampaignTierCard
            key={tier}
            tier={tier}
            title={intl.formatMessage({
              id: 'PGV5V2cE7veTAtL4T2ZS',
              defaultMessage: '{tierName} Benefits',
              description: 'Title for a tier benefit card on the tier upgrade campaign page',
            }, { tierName: getLoyaltyTierDisplayName(tier) })}
            subtitle={eligibilityLabel}
            benefits={tierBenefitsMap[tier] ?? []}
          />)}
        </CardsGrid>
        {onCtaClick && <Typography align="center">
          <TextButton kind="primary" size="large" onClick={onCtaClick}>
            {ctaText}
          </TextButton>
        </Typography>}
      </VerticalSpacer>
    </LayoutContainer>
  </Section>
}

export default TierUpgradeCampaignTierCards
